import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import Tooltip from '@/components/Tooltip';
import { BORDER_RADIUS } from '@/constants/config';
import copyText from '@/constants/utils/copy_text';
import { useToastStore } from '@/store';
import { getMemoryThemeColors } from './memoryTheme';

const isWeb = Platform.OS === 'web';

/**
 * MemoryHero — masthead with headline, actions, and a quiet stats strip.
 * Color is reserved for the brand mark and the live status dot.
 */
function MemoryHero({
  memories = [],
  learnedCount = 0,
  manualCount = 0,
  isMemoryEnabled = true,
  isLearningEnabled = true,
  onOpenAdd,
  isAdding = false,
  isCreating = false,
  onOpenBuilder,
  onOpenImport,
}) {
  const { colors, isDark } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const [isExporting, setIsExporting] = useState(false);

  const memColors = useMemo(() => getMemoryThemeColors(isDark, colors), [isDark, colors]);
  const styles = useMemo(
    () => createStyles(colors, memColors, isXS, isSM),
    [colors, memColors, isXS, isSM]
  );

  const handleExport = useCallback(async () => {
    if (!memories.length) return;
    setIsExporting(true);
    try {
      const formatted = memories
        .map((m, idx) => {
          const source = m.source || (m.isAutoGenerated ? 'auto' : 'manual');
          const sourceLabel =
            source === 'explicit' ? t('settings.memory.sourceExplicit')
              : source === 'dream' ? t('settings.memory.sourceDream')
              : source === 'auto' ? t('settings.memory.sourceAuto')
              : source === 'import' ? t('settings.memory.sourceImport')
              : t('pages.memory.manualCount');
          return `${idx + 1}. [${sourceLabel}] ${m.content}`;
        })
        .join('\n\n');

      await copyText(`# Gab AI Memories (${memories.length} total)\n\n${formatted}\n`);
      addToast(
        t('pages.memory.allMemoriesExported').replace('%COUNT%', memories.length),
        'success'
      );
    } catch {
      addToast(t('common.failedToCopy') || 'Failed to copy memories', 'error');
    } finally {
      setTimeout(() => setIsExporting(false), 1500);
    }
  }, [memories, addToast, t]);

  const totalCount = memories.length;

  const renderSecondary = (label, icon, onPress, active = false) => (
    <Tooltip label={label} showIsXs>
      <Pressable
        style={({ hovered }) => [
          styles.secondaryButton,
          hovered && styles.secondaryButtonHovered,
        ]}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={label}
      >
        <Icon
          name={icon}
          size={13}
          color={active ? memColors.success : colors.text_color_secondary}
        />
        {(
          <Text size="s" weight="medium" color="primary">
            {label}
          </Text>
        )}
      </Pressable>
    </Tooltip>
  );

  return (
    <View style={styles.container}>
      <View style={styles.topRow}>
        <View style={styles.headerLeft}>
          <View style={styles.eyebrowRow}>
            <View style={styles.brandMark}>
              <Icon name="brain" size={12} color={memColors.accent} />
            </View>
            <Text size="xxs" weight="bold" color="tertiary" style={styles.eyebrowText}>
              {t('pages.memory.eyebrow')}
            </Text>
          </View>

          <Text tag="h1" weight="extraBold" style={styles.headline}>
            {t('pages.memory.headline')}
          </Text>

          <Text size="n" color="secondary" style={styles.subtitle}>
            {t('pages.memory.subtitle')}
          </Text>
        </View>

        <View style={styles.actionsWrap}>
          <Button
            text={isAdding ? t('common.cancel') : t('pages.memory.addMemory')}
            prependIcon={isAdding ? 'xmark' : 'plus'}
            variant="primary"
            size={isXS ? 'small' : 'medium'}
            isDisabled={isCreating}
            onPress={onOpenAdd}
          />
          <View style={styles.secondaryRow}>
            {renderSecondary(t('pages.memory.guidedSetup'), 'wand-magic-sparkles', onOpenBuilder)}
            {renderSecondary(t('pages.memory.importFromAI'), 'file-import', onOpenImport)}
            {totalCount > 0 && renderSecondary(
              isExporting ? t('pages.memory.copied') : t('pages.memory.exportMemories'),
              isExporting ? 'check' : 'copy',
              handleExport,
              isExporting,
            )}
          </View>
        </View>
      </View>

      {totalCount > 0 && (
        <View style={styles.statsStrip}>
          <View style={styles.stat}>
            <Text size="l" weight="extraBold" color="primary" style={styles.statNumber}>
              {totalCount}
            </Text>
            <Text size="xs" color="secondary">{t('pages.memory.totalMemories')}</Text>
          </View>
          <View style={styles.statDivider} />
          <View style={styles.stat}>
            <Text size="l" weight="extraBold" color="primary" style={styles.statNumber}>
              {learnedCount}
            </Text>
            <Text size="xs" color="secondary">{t('pages.memory.learnedCount')}</Text>
          </View>
          <View style={styles.statDivider} />
          <View style={styles.stat}>
            <Text size="l" weight="extraBold" color="primary" style={styles.statNumber}>
              {manualCount}
            </Text>
            <Text size="xs" color="secondary">{t('pages.memory.manualCount')}</Text>
          </View>
          <View style={styles.statDivider} />
          <View style={[styles.stat, styles.statStatus]}>
            <View
              style={[
                styles.statusDot,
                { backgroundColor: isMemoryEnabled ? memColors.success : memColors.warning },
              ]}
            />
            <View style={{ minWidth: 0 }}>
              <Text size="xs" weight="bold" color="primary">
                {isMemoryEnabled ? t('pages.memory.statusActive') : t('pages.memory.statusPaused')}
              </Text>
              <Text size="xxs" color="secondary">
                {isLearningEnabled ? t('pages.memory.statusLearning') : t('pages.memory.learningPaused')}
              </Text>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

const createStyles = (colors, memColors, isXS, isSM) =>
  StyleSheet.create({
    container: {
      width: '100%',
      paddingBottom: isXS ? 14 : 20,
    },
    topRow: {
      flexDirection: isSM || isXS ? 'column' : 'row',
      alignItems: isSM || isXS ? 'stretch' : 'flex-start',
      justifyContent: 'space-between',
      gap: isXS ? 16 : 24,
      flexWrap: 'wrap',
      marginBottom: isXS ? 16 : 22,
    },
    headerLeft: { flexGrow: 1, flexBasis: isSM || isXS ? 'auto' : 320, minWidth: 0, gap: 6 },
    eyebrowRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 2,
    },
    brandMark: {
      width: 22,
      height: 22,
      borderRadius: 7,
      backgroundColor: memColors.accentBg,
      alignItems: 'center',
      justifyContent: 'center',
    },
    eyebrowText: {
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    headline: {
      fontSize: isXS ? 26 : 32,
      lineHeight: isXS ? 32 : 38,
      letterSpacing: -0.6,
      color: colors.text_color_primary,
    },
    subtitle: { maxWidth: 640, lineHeight: 22 },
    actionsWrap: {
      flexDirection: 'column',
      alignItems: isSM || isXS ? 'stretch' : 'flex-end',
      gap: 10,
      flexShrink: 0,
    },
    secondaryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
      justifyContent: isSM || isXS ? 'flex-start' : 'flex-end',
    },
    secondaryButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      minHeight: 40,
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      ...(isWeb ? { cursor: 'pointer', transition: 'all 0.16s ease' } : {}),
    },
    secondaryButtonHovered: {
      backgroundColor: colors.solid_color_secondary,
      borderColor: colors.border_color_input,
    },
    statsStrip: {
      flexDirection: 'row',
      flexWrap: isXS ? 'wrap' : 'nowrap',
      alignItems: 'stretch',
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      overflow: 'hidden',
    },
    stat: {
      flex: 1,
      minWidth: isXS ? '50%' : 0,
      paddingVertical: 12,
      paddingHorizontal: 16,
      gap: 1,
    },
    statNumber: {
      fontVariant: ['tabular-nums'],
      letterSpacing: -0.4,
    },
    statStatus: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    statDivider: {
      width: StyleSheet.hairlineWidth,
      backgroundColor: colors.border_color_secondary,
      ...(isXS ? { display: 'none' } : {}),
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      flexShrink: 0,
    },
  });

export default React.memo(MemoryHero);
